import NoDataIcon from "@/assets/icons/no-data.svg"
import { SelectedReserveType } from "@/atoms/shared"
import { RelendDialog } from "@/components/relendDialog"
import { NFTDetailType } from "@/hooks/useFetchNFT"
import useTrans from "@/hooks/useTrans"
import { Box, Grid, RadioGroup, Tab, Tabs, Typography } from "@mui/material"
import Stack from "@mui/material/Stack"
import Image from "next/image"
import React, { useCallback, useMemo, useState } from "react"
import NFTItem from "./NFTItem"
import SearchInput from "./SearchInput"

import * as styles from "../styles"

type NFTVoucherTab = "available" | "claimed"

interface ClaimDialogProps {
  title: string
  isOpen: boolean
  handleCloseModal: () => void
  availableClaimNFTs?: NFTDetailType[]
  claimedNFTs?: NFTDetailType[]
  handleSearchText?: (text: string) => void
  handleSelectNFTChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  handleClaimClick: (mint: string) => void
  selectedReserve?: SelectedReserveType
  shouldRefreshCheckbox: boolean
  nftMintSelected?: string
  setNftMintSelected: React.Dispatch<React.SetStateAction<string | undefined>>
}

const ClaimDialog = ({
  isOpen,
  handleCloseModal,
  handleClaimClick,
  title,
  availableClaimNFTs,
  claimedNFTs,
  handleSearchText,
  handleSelectNFTChange,
  selectedReserve,
  shouldRefreshCheckbox,
  nftMintSelected,
  setNftMintSelected,
}: ClaimDialogProps) => {
  const trans = useTrans()
  const [tab, setTab] = useState<NFTVoucherTab>("available")

  const handleTabChange = useCallback(
    (_event: React.SyntheticEvent, newTab: NFTVoucherTab) => {
      setTab(newTab)
    },
    [setTab],
  )

  const listNFTs = useMemo(() => {
    if (tab === "available") return availableClaimNFTs
    else return claimedNFTs
  }, [tab, availableClaimNFTs, claimedNFTs])

  return (
    <RelendDialog
      open={isOpen}
      handleClose={handleCloseModal}
      title={title}
      maxWidth='md2'
      data-testid='modal-choose-nft-to-claim'
    >
      <Stack alignItems='center' gap={2} className='w-full'>
        <Tabs
          value={tab}
          onChange={handleTabChange}
          TabIndicatorProps={{ sx: { display: "none" } }}
          aria-label='tabs-nft-claim'
          variant='fullWidth'
          className='bg-white border border-solid border-secondary rounded-lg p-[2px] w-full'
          id='nft-claim-tabs'
        >
          <Tab
            classes={{ selected: "bg-secondary !text-primary font-semibold rounded-lg" }}
            className='flex-1 py-2 text-sm sm:text-lg font-medium text-neutral-500'
            value='available'
            label={trans.dashboard.hero.available_to_claim_tab(availableClaimNFTs?.length || 0)}
            data-testid='nft-available-to-claim-tab'
          />
          <Tab
            classes={{ selected: "bg-secondary !text-primary font-semibold rounded-lg" }}
            className='flex-1 py-2 text-sm sm:text-lg font-medium text-neutral-500'
            value='claimed'
            label={trans.dashboard.hero.claimed_tab(claimedNFTs?.length || 0)}
            data-testid='nft-claimed-tab'
          />
        </Tabs>

        {handleSearchText && (
          <SearchInput
            handleSearchText={handleSearchText}
            placeholder={trans.dashboard.modal.search_by_name_or_address}
          />
        )}

        {listNFTs && listNFTs.length > 0 && (
          <RadioGroup sx={{ width: "100%" }} name='select-nft-group' onChange={handleSelectNFTChange}>
            <Grid container spacing={2}>
              {listNFTs.map((item, index) => (
                <Grid key={`${item.name}-${item.mintAddress}-${index}`} item xs={6} sm={4} md={3}>
                  <NFTItem
                    nftDetail={item}
                    shouldRefreshCheckbox={shouldRefreshCheckbox}
                    selectedReserve={selectedReserve}
                    nftMintSelected={nftMintSelected}
                    setNftMintSelected={setNftMintSelected}
                    handleClaimClick={tab === "available" ? handleClaimClick : undefined}
                    showMintAddress={tab === "claimed"}
                    isClaimingVoucher={true}
                    showSelected={false}
                  />
                </Grid>
              ))}
            </Grid>
          </RadioGroup>
        )}

        {!listNFTs || (listNFTs && listNFTs.length === 0) ? (
          <Box sx={{ margin: "32px" }}>
            <Image src={NoDataIcon} alt='no-data-icon' width={161} height={161} />
            <Typography sx={styles.typoNoData}>{trans.dashboard.modal.no_nft_to_send}</Typography>
          </Box>
        ) : null}
      </Stack>
    </RelendDialog>
  )
}

export default ClaimDialog
